'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';

interface FeedbackContextType {
  isOpen: boolean;
  playableId: number | undefined;
  openFeedback: (playableId?: number) => void;
  closeFeedback: () => void;
  toggleFeedback: () => void;
  onSubmitSuccess: (() => void) | null;
  setOnSubmitSuccess: (callback: (() => void) | null) => void;
  notifySubmitSuccess: () => void;
}

const FeedbackContext = createContext<FeedbackContextType | undefined>(undefined);

export function FeedbackProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [playableId, setPlayableId] = useState<number | undefined>(undefined);
  const [onSubmitSuccess, setOnSubmitSuccess] = useState<(() => void) | null>(null);

  const openFeedback = useCallback((id?: number) => {
    setPlayableId(id);
    setIsOpen(true);
  }, []);

  const closeFeedback = useCallback(() => {
    setIsOpen(false);
    // Clear playableId when closing
    setPlayableId(undefined);
  }, []);

  const notifySubmitSuccess = useCallback(() => {
    if (onSubmitSuccess) {
      onSubmitSuccess();
    }
  }, [onSubmitSuccess]);

  const toggleFeedback = useCallback(() => setIsOpen(prev => !prev), []);

  return (
    <FeedbackContext.Provider
      value={{
        isOpen,
        playableId,
        openFeedback,
        closeFeedback,
        toggleFeedback,
        onSubmitSuccess,
        setOnSubmitSuccess,
        notifySubmitSuccess
      }}
    >
      {children}
    </FeedbackContext.Provider>
  );
}

export function useFeedback() {
  const context = useContext(FeedbackContext);
  if (context === undefined) {
    throw new Error('useFeedback must be used within a FeedbackProvider');
  }
  return context;
}
